/** @jsx jsx */
import { jsx, Styled } from "theme-ui"
import React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import Pluralize from "pluralize"
import { connect } from "react-redux"
import { client, updateLinesInCart, removeLinesFromCart, getCart } from "@shop/helpers"
import LineItem from "@shop/components/LineItem"
import { rem } from "@src/theme"
import Button from "@components/Button"
import IconLink from "@components/IconLink"
import Cross from "@components/assets/svg/cross.svg"
import { Themed } from "@theme-ui/mdx"
import { getNewCheckoutPromise } from "@shop/helpers"

const CartModal = ({
  checkout,
  cart,
  removeLineItem,
  closeCartModal,
  onClose,
  ...props
}) => {
  if (checkout === null || checkout === undefined) {
    getNewCheckoutPromise().then(value => {
      props.setCheckout(value)
    })
    return null
  }

  const [items, setItems] = useState(checkout ? (checkout.lines?.edges?.map(edge => edge.node) || checkout.lineItems || []) : [])
  const [isUpdatingQuantities, setIsUpdatingQuantities] = useState(false)
  const [unsavedChanges, setUnsavedChanges] = useState(new Set())
  const wrapperRef = useRef(null)

  const twentyDiscountedApplied = checkForTwentyPercentDiscount(checkout)
  const lineItemsSubtotal = getLineItemsSubtotal(checkout)
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closeCartModal()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    document.onkeydown = e => {
      if (e.key === "Escape" || e.keyCode === 27) {
        onClose()
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.onkeydown = () => { }
    }
  }, [wrapperRef])

  // Simplify your useEffect - always use server state when available
  useEffect(() => {

    if (checkout && !isUpdatingQuantities) {
      const serverItems = checkout.lines?.edges?.map(edge => edge.node) || checkout.lineItems || []

      setItems(currentItems => {
        // For items with unsaved changes, preserve the local quantity
        const updatedItems = serverItems.map(serverItem => {
          if (unsavedChanges.has(serverItem.id)) {
            const currentItem = currentItems.find(item => item.id === serverItem.id)
            if (currentItem) {
              return { ...serverItem, quantity: currentItem.quantity }
            }
          }
          return serverItem
        })

        return updatedItems
      })
    }
  }, [checkout, isUpdatingQuantities, unsavedChanges])

  const handleCheckout = async () => {
    if (unsavedChanges.size > 0) {
      await syncUnsavedChanges()
    }
    closeCartModal()
    window.open(checkout.checkoutUrl || checkout.webUrl)
  }

  const handleQuantityChange = ({ id, quantity, productId }) => {

    // Allow empty string and basic numeric validation
    if (quantity !== '' && (isNaN(quantity) || parseInt(quantity, 10) < 0)) {
      return
    }

    const limit = props.itemLimits.find(i => i.id === productId)?.limit
    const numericQuantity = quantity === '' ? 0 : parseInt(quantity, 10)

    let finalQuantity = quantity
    if (limit && numericQuantity > limit) {
      finalQuantity = limit.toString()
    }

    // Find the line item that corresponds to this variant/merchandise ID
    const lineItem = items.find(item => {
      const variantId = item?.variant?.id || item?.merchandise?.id
      return variantId === id
    })

    if (!lineItem) {
      return
    }

    const lineItemId = lineItem.id

    // Mark this LINE ITEM as having unsaved changes (not the variant)
    setUnsavedChanges(prev => {
      const newSet = new Set(prev).add(lineItemId)
      return newSet
    })

    // Update local state immediately using LINE ITEM ID
    setItems(currentItems => {
      const updatedItems = currentItems.map(item => {
        if (item.id === lineItemId) {
          return { ...item, quantity: finalQuantity }
        }
        return item
      })
      return updatedItems
    })
  }

  // Simplify handleLineItemRemove - no optimistic update
  const handleLineItemRemove = (id, variantId, productId) => {

    // Remove from unsaved changes if it was being edited
    setUnsavedChanges(prev => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })

    // Call the Redux action which should handle the server-side removal
    // The useEffect will update the UI when the server responds
    removeLineItem({ id, variantId, productId })
  }

  const syncItemQuantity = useCallback(async (itemId) => {

    if (!unsavedChanges.has(itemId)) {
      return
    }

    if (isUpdatingQuantities) {
      return
    }

    const item = items.find(i => i.id === itemId)
    if (!item) {
      return
    }


    try {
      setIsUpdatingQuantities(true)

      const res = await updateLinesInCart(
        props.checkoutId,
        [{
          id: itemId,
          quantity: parseInt(item.quantity || '0', 10),
        }]
      )

      setUnsavedChanges(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })

      props.setCheckout(res)

    } catch (error) {
      console.error('❌ Failed to sync item quantity:', error)

      try {
        const freshCart = await getCart(props.checkoutId)
        if (freshCart) {
          const freshItems = freshCart.lines?.edges?.map(edge => edge.node) || freshCart.lineItems || []
          setItems(freshItems)
          props.setCheckout(freshCart)
          setUnsavedChanges(prev => {
            const newSet = new Set(prev)
            newSet.delete(itemId)
            return newSet
          })
        }
      } catch (refreshError) {
        console.error('Failed to refresh cart after error:', refreshError)
      }
    } finally {
      setIsUpdatingQuantities(false)
    }
  }, [items, unsavedChanges, isUpdatingQuantities, props.checkoutId, props.setCheckout])

  const syncUnsavedChanges = useCallback(async () => {
    if (unsavedChanges.size === 0 || isUpdatingQuantities) return

    try {
      setIsUpdatingQuantities(true)
      const changedItems = items.filter(item => unsavedChanges.has(item.id))

      const res = await updateLinesInCart(
        props.checkoutId,
        changedItems.map(item => ({
          id: item.id,
          quantity: parseInt(item.quantity || '0', 10),
        }))
      )

      setUnsavedChanges(new Set())
      props.setCheckout(res)

    } catch (error) {
      console.error('Failed to sync unsaved changes:', error)

      try {
        const freshCart = await getCart(props.checkoutId)
        if (freshCart) {
          const freshItems = freshCart.lines?.edges?.map(edge => edge.node) || freshCart.lineItems || []
          setItems(freshItems)
          props.setCheckout(freshCart)
          setUnsavedChanges(new Set())
        }
      } catch (refreshError) {
        console.error('Failed to refresh cart after error:', refreshError)
      }
    } finally {
      setIsUpdatingQuantities(false)
    }
  }, [items, unsavedChanges, isUpdatingQuantities, props.checkoutId, props.setCheckout])

  const handleQuantityBlur = useCallback((itemId) => {

    // Find the line item that corresponds to this variant/merchandise ID
    const lineItem = items.find(item => {
      const variantId = item?.variant?.id || item?.merchandise?.id
      return variantId === itemId
    })

    if (!lineItem) {
      return
    }

    const lineItemId = lineItem.id

    if (unsavedChanges.has(lineItemId)) {
      syncItemQuantity(lineItemId)
    }
  }, [items, syncItemQuantity, unsavedChanges])

  const clearBag = async () => {
    const fetchedCheckout = await fetchCheckout(props.checkoutId)
    if (!fetchedCheckout) {
      props.resetCheckout()
      return
    }

    setUnsavedChanges(new Set())
    setItems([])
    props.resetItemLimits()

    try {
      const res = await removeLinesFromCart(
        props.checkoutId,
        items.map(i => i.id)
      )
      props.setCheckout(res)
    } catch (error) {
      console.error('Failed to clear cart:', error)
      setItems(items)
      const freshCheckout = await fetchCheckout(props.checkoutId)
      if (freshCheckout) {
        props.setCheckout(freshCheckout)
        const freshItems = freshCheckout.lines?.edges?.map(edge => edge.node) || freshCheckout.lineItems || []
        setItems(freshItems)
      }
    }
  }

  const fetchCheckout = async checkoutId => {
    const res = await getCart(checkoutId)
    return res
  }

  const lineItems = items.map(item => {
    const itemProductId = item?.variant?.product?.id || item?.merchandise?.product?.id
    const allVariants = items.filter(i => {
      const checkItemProductId = i?.variant?.product?.id || i?.merchandise?.product?.id
      return checkItemProductId == itemProductId
    })

    let totalQty = 0
    if (allVariants.length) {
      totalQty = allVariants.reduce((sum, obj) => {
        const qty = typeof obj.quantity === 'string' ? parseInt(obj.quantity || '0', 10) : obj.quantity
        return sum + (isNaN(qty) ? 0 : qty)
      }, 0)
    }

    const hasUnsavedChanges = unsavedChanges.has(item.id)

    return (
      <LineItem
        key={item.id}
        item={item}
        onRemove={handleLineItemRemove}
        onQuantityChange={handleQuantityChange}
        quantityValue={item.quantity}
        onQuantityBlur={() => handleQuantityBlur(item?.variant?.id || item?.merchandise?.id)}
        limit={
          props.itemLimits.find(obj => obj.id == (item?.variant?.product?.id || item?.merchandise?.product?.id))
            ?.limit
        }
        totalQty={totalQty}
      />
    )
  })

  const isAnyLineItems = lineItems.length > 0

  return (
    <div ref={wrapperRef}>
      <div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Themed.h4 sx={{ my: 0 }}>
          {isAnyLineItems && (
            <React.Fragment>
              <span sx={{ color: "purple" }}>
                {lineItems.length}{" "}
              </span>
              {Pluralize("item", lineItems.length)} in your bag!
            </React.Fragment>
          )}
        </Themed.h4>
        <button sx={{ cursor: "pointer" }}>
          <Cross
            onClick={() => {
              closeCartModal()
              onClose()
            }}
          />
        </button>
      </div>
      {isAnyLineItems && (
        <React.Fragment>
          <div sx={{ borderBottom: `2px solid rgba(58, 42, 85, 0.3)` }}>
            <div sx={{ display: "flex", justifyContent: "space-between" }}>
              <div sx={{ display: "flex", width: [rem(200), null, rem(280)] }}>
                <Themed.h5>Item</Themed.h5>
              </div>
              <Themed.h5 sx={{ fontSize: rem(18), display: ["none", "block"] }}>
                Price
              </Themed.h5>
            </div>
          </div>

          <div>{lineItems}</div>

          <div
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >

            {twentyDiscountedApplied && <div sx={{ mb: rem(20), mt: rem(20), display: "flex", alignItems: "center" }}>

              <p sx={{ mr: 5, my: 0, fontSize: rem(16) }}>
                Subtotal
              </p>{" "}

              {twentyDiscountedApplied && <p sx={{ my: 0, transform: "translate(-5px, -1px)", fontSize: rem(16), }}>
                ${lineItemsSubtotal}
              </p>
              }

            </div>}



            {twentyDiscountedApplied && <div sx={{ mb: rem(20), mt: rem(0), display: "flex", alignItems: "center" }}>

              <p sx={{ mr: 7, my: 0, fontSize: rem(16), color: 'red' }}>
                20% discount for orders over $300
              </p>{" "}

              {twentyDiscountedApplied && <p sx={{ my: 0, transform: "translate(-5px, -2px)", color: 'red', fontSize: rem(16), }}>
                -${(lineItemsSubtotal - checkout.estimatedCost.totalAmount.amount).toFixed(2)}
              </p>
              }

            </div>
            }
            <div sx={{ mb: rem(20), mt: twentyDiscountedApplied ? rem(0) : rem(20), display: "flex", alignItems: "center" }}>

              <Themed.h5 sx={{ mr: 5, my: 0, fontSize: rem(18) }}>
                Total
              </Themed.h5>{" "}
              {/* {twentyDiscountedApplied && <p sx={{ my: 0, transform: "translate(-5px, -3px)",  color: "rgba(0,0,0,0.3)" }}>
                ${checkout.lineItemsSubtotalPrice.amount}
              </p>
              } */}
              <p sx={{
                my: 0, transform: "translateY(-3px)",
                transition: "opacity 0.3s ease-in-out",
                opacity: isUpdatingQuantities ? 0.5 : 1

              }}>
                ${(checkout.estimatedCost?.totalAmount?.amount || '0.00')}
              </p>
            </div>
            <div sx={{ display: "flex", alignItems: "center" }}>
              <button
                sx={{
                  variant: "typography.link",
                  textDecoration: "underline",
                  mr: 3,
                  cursor: "pointer",
                }}
                onClick={clearBag}
              >
                Clear bag
              </button>

              <Button onClick={handleCheckout} bg="darkPurple" color="white">
                Checkout
              </Button>
            </div>
          </div>
        </React.Fragment>
      )}

      {!isAnyLineItems && (
        <div>
          <Themed.h4 sx={{ textAlign: "center" }}>Your cart is empty</Themed.h4>
          <div sx={{ display: "flex", justifyContent: "space-around", flexDirection: ["column", "row"] }}>
            <IconLink link={"/shop/pride"} label="Shop pride" type="arrowRight" onClick={closeCartModal} />
            <IconLink link={"/shop/school"} label="Shop school" type="arrowRight" onClick={closeCartModal} />
            <IconLink link={"/shop/workplace"} label="Shop workplace" type="arrowRight" onClick={closeCartModal} />
          </div>
        </div>
      )}
    </div>
  )
}

export default connect(
  state => ({
    cart: state.cart,
    checkout: state.cart.checkout,
    checkoutId: state.cart.checkoutId,
    itemLimits: state.cart.itemLimits,
  }),
  dispatch => ({
    addVariantToCart: dispatch.cart.addVariantToCart,
    removeLineItem: dispatch.cart.removeLineItem,
    closeProductModal: () => dispatch.cartModal.toggleProductModal(false),
    closeCartModal: () => dispatch.cartModal.toggleCartModal(false),
    setCheckout: dispatch.cart.setCheckout,
    resetCheckout: dispatch.cart.resetCheckout,
    resetItemLimits: dispatch.cart.resetItemLimits,
  })
)(CartModal)

const checkForTwentyPercentDiscount = (checkout) => {
  if (!checkout) return false

  // Use the same calculation as your subtotal function
  const originalSubtotal = parseFloat(getLineItemsSubtotal(checkout))
  const currentTotal = parseFloat(checkout.estimatedCost?.totalAmount?.amount || 0)

  if (originalSubtotal > 0) {
    const discountAmount = originalSubtotal - currentTotal
    const discountPercentage = (discountAmount / originalSubtotal) * 100

    return discountPercentage >= 19.5 // Allow for small rounding differences
  }

  return false
}

// Simple function to calculate true subtotal from line items
const getLineItemsSubtotal = (checkout) => {
  if (!checkout?.lines?.edges) return '0.00'

  const subtotal = checkout.lines.edges.reduce((total, edge) => {
    const item = edge.node
    const quantity = parseInt(item.quantity || 0, 10)
    const unitPrice = parseFloat(
      item.merchandise?.price.amount ||
      0
    )

    return total + (quantity * unitPrice)
  }, 0)

  return subtotal.toFixed(2)
}